/*
Badges screen — mirror Android BadgesScreen.kt : grille 3 colonnes,
header « X / 60 débloqués » (badges entiers), sections « Débloqués » /
« À débloquer ». Catalogue = binaires statiques + Familier genre/arr +
paliers Pokédex.

Les 3 cartes progressives Marcheur / Botaniste / Chasseur (héritage du
dev d'origine, sans équivalent Android) ne sont plus rendues ici —
parité stricte. Leurs defs restent dans data/badge.ts : l'évaluateur et
la preview Profil s'en servent.
*/

import { navigate, kPaths } from '../router'
import { listCaptures } from '../data/captureRepository'
import { ensureArbresFeatures } from '../data/arbresFeatures'
import { ensureArbreAttrs } from '../data/arbreAttrs'
import { ensureSpeciesInfo } from '../data/speciesInfo'
import { ensureSpeciesIndex } from '../data/speciesIndex'
import { ensureGenreInfo } from '../data/genreInfo'
import { ensureArrSpecies } from '../data/arrSpecies'
import { evaluateBadges } from '../data/badgeEvaluator'
import {
	glyphFor,
	POKEDEX_PREFIX,
	type BadgeCategory,
	type BadgeState,
	type BinaryBadgeDef,
	type BinaryBadgeState,
} from '../data/badge'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonBadgeTile } from './components/Skeleton'

// Date courte « 07/06/2026 » — parité Android DateFormat.SHORT.
const kFrenchDateShort = new Intl.DateTimeFormat('fr-FR')

/*
Mount the badges screen. Renders a static shell synchronously, then
hydrates the unlock state async.
*/
export function mountBadgesScreen(inRoot: HTMLElement): void
{
	// Skeleton tiles roughly match the above-fold badge-grid count.
	// Capped to 24 (fits one viewport on a phone) so the loading view
	// doesn't allocate 60+ DOM nodes for a screen that'll be replaced
	// once the catalog is hydrated.
	const vSkeletonTiles = Array.from({ length: 24 }, () => renderSkeletonBadgeTile()).join('')
	inRoot.innerHTML = `
		<div class="screen badges">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Badges &amp; succès</h1>
			</header>
			<div class="arboretum-subheader" id="badges-subheader">
				${renderSkeletonBlock({ width: '160px', height: '14px' })}
			</div>
			<div class="badges-grid-wrap" id="badges-wrap">
				<div class="badges-grid skeleton-badge-grid">${vSkeletonTiles}</div>
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.profile())
	})
	void hydrate(inRoot)
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	const vSubheader = inRoot.querySelector<HTMLElement>('#badges-subheader')
	const vWrap = inRoot.querySelector<HTMLElement>('#badges-wrap')
	if (!vSubheader || !vWrap) return

	try
	{
		// Load all 6 datasets in parallel before evaluating — fullCatalog
		// needs speciesIndex + genreInfo + arrSpecies, the evaluator
		// additionally needs arbreAttrs (Familier arr + Géant / Bonsaï…)
		// and speciesInfo (raretés exactes 1..5).
		const [vCaptures, vFeatures, vInfo] = await Promise.all([
			listCaptures(),
			ensureArbresFeatures(),
			ensureSpeciesInfo(),
			ensureSpeciesIndex(),
			ensureGenreInfo(),
			ensureArrSpecies(),
			ensureArbreAttrs(),
		])
		const vById = new Map<number, typeof vFeatures[number]>()
		for (const vCap of vCaptures)
		{
			const vF = vFeatures.find((inF) => inF.id === vCap.arbreId)
			if (vF) vById.set(vCap.arbreId, vF)
		}

		const vStates = evaluateBadges({ captures: vCaptures, arbresById: vById, speciesInfo: vInfo })
		// « X / 60 débloqués » — badges entiers, parité Android
		// (`unlocked.size / catalog.size`). Les progressifs PWA-only ne
		// comptent pas.
		const vBinaries = vStates.filter((inS): inS is BinaryBadgeState => inS.kind === 'binary')
		const vUnlockedCount = vBinaries.filter((inS) => inS.unlockedAt !== null).length

		vSubheader.innerHTML = `
			<span class="arboretum-counter">
				<strong>${vUnlockedCount}</strong> / ${vBinaries.length} débloqués
			</span>
		`

		vWrap.innerHTML = renderSections(vStates)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vMsg)}</span>`
		vWrap.innerHTML = ''
	}
}

/*
Render the full screen body : sections « Débloqués » / « À débloquer »
(grille 3 colonnes, parité Android GridCells.Fixed(3)).

L'ordre des catégories à l'intérieur de la section verrouillée reste
stable pour éviter les sauts visuels entre 2 hydrates successifs.
*/
const kCategoryOrder: readonly BadgeCategory[] = [
	'decouverte', 'botanique', 'geographie', 'remarquables', 'demesure',
]

function renderSections(inStates: BadgeState[]): string
{
	const vBinaries = inStates.filter((inS): inS is BinaryBadgeState => inS.kind === 'binary')

	const vUnlocked = vBinaries.filter((inS) => inS.unlockedAt !== null)
	const vLocked = vBinaries.filter((inS) => inS.unlockedAt === null)

	// Tri : Débloqués par unlockedAt desc, À débloquer par catégorie puis label.
	vUnlocked.sort((a, b) => (b.unlockedAt ?? 0) - (a.unlockedAt ?? 0))
	vLocked.sort((a, b) => {
		const vCatA = kCategoryOrder.indexOf(a.def.category)
		const vCatB = kCategoryOrder.indexOf(b.def.category)
		if (vCatA !== vCatB) return vCatA - vCatB
		return a.def.label.localeCompare(b.def.label)
	})

	// Parité Android : EmptyState quand rien n'est débloqué, la section
	// « À débloquer » reste visible en dessous.
	const vUnlockedHtml = vUnlocked.length > 0
		? `<section class="badges-section">
				<h2 class="badges-section-title">Débloqués</h2>
				<div class="badges-grid">${vUnlocked.map(renderBinaryCard).join('')}</div>
			</section>`
		: renderEmptyState({
			variant: 'badges',
			title: 'Aucun badge débloqué',
			body: 'Capture ton premier arbre : « Première capture » se débloque aussitôt. Ensuite, espèces ultra-rares, géants de plus de 30 m et jeunes pousses t\'attendent.',
		})

	const vLockedHtml = vLocked.length > 0
		? `<section class="badges-section">
				<h2 class="badges-section-title">À débloquer</h2>
				<div class="badges-grid">${vLocked.map(renderBinaryCard).join('')}</div>
			</section>`
		: ''

	return vUnlockedHtml + vLockedHtml
}

function renderBinaryCard(inState: BinaryBadgeState): string
{
	const vUnlocked = inState.unlockedAt !== null
	// Date seule format court — parité Android (« 07/06/2026 »).
	const vUnlockedDate = vUnlocked ? kFrenchDateShort.format(new Date(inState.unlockedAt as number)) : ''
	const vBlocked = !inState.def.implemented
	const vSubtitle = vBlocked ? 'Donnée non disponible' : inState.def.description
	const vGlyphHtml = renderBinaryGlyph(inState.def)

	return `
		<div class="badge-card" data-unlocked="${vUnlocked ? 'true' : 'false'}" data-blocked="${vBlocked ? 'true' : 'false'}">
			${vGlyphHtml}
			<div class="badge-label">${escapeHtml(inState.def.label)}</div>
			<div class="badge-criterion">${escapeHtml(vSubtitle)}</div>
			${vUnlocked ? `<div class="badge-unlocked-at">${vUnlockedDate}</div>` : '<div class="badge-locked-pill">🔒</div>'}
		</div>
	`
}

/*
Glyph rendu pour les badges binaires. Cas spécial Pokédex (v1.4.0) : on
empile 📖 et le chiffre seuil dans le même cercle (mirror Android
BadgeVisual.VectorWithBadge). Autres badges : emoji simple via glyphFor.
*/
function renderBinaryGlyph(inDef: BinaryBadgeDef): string
{
	if (inDef.id.startsWith(POKEDEX_PREFIX))
	{
		const vThreshold = inDef.id.slice(POKEDEX_PREFIX.length)
		return `
			<div class="badge-glyph badge-glyph-pokedex" aria-hidden="true">
				<span class="badge-glyph-pokedex-icon">📖</span>
				<span class="badge-glyph-pokedex-threshold">${escapeHtml(vThreshold)}</span>
			</div>
		`
	}
	return `<div class="badge-glyph" aria-hidden="true">${glyphFor(inDef)}</div>`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
